/**
 * XML → YAML Tool
 *
 * Converts XML to YAML by parsing to a JS object and serializing with the yaml library.
 */

import { XMLParser } from 'fast-xml-parser';
import { stringify as stringifyYaml } from 'yaml';

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

export const XmlToYamlTool: ITransformerTool = {
  id: 'xml-to-yaml',
  name: 'XML → YAML',
  description: 'Convert XML to YAML',
  version: '1.0.0',
  type: 'transformer',
  category: 'Converters',
  keywords: ['xml', 'yaml', 'convert', 'transform'],

  seo: {
    slug: 'xml-to-yaml',
    pageTitle: 'XML to YAML Converter - Free Online Tool | SWE DevTools',
    metaDescription: 'Convert XML to YAML format instantly. Preserves attributes and nested structures. Fast, private, runs entirely in your browser.',
    keywords: ['xml to yaml', 'xml to yaml converter', 'convert xml to yaml online', 'xml yaml transformer'],
    relatedTools: ['yaml-to-xml', 'xml-to-json', 'json-to-yaml'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.XML, SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.YAML,

  attributes: [
    {
      name: 'indent',
      type: 'select',
      label: 'Indent Size',
      defaultValue: '2',
      options: ['2', '4'],
      description: 'Number of spaces for YAML indentation',
    },
  ],

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const text = String(input ?? '').trim();
    const indent = parseInt((config.indent as string) || '2', 10);

    let parsed: unknown;
    try {
      const parser = new XMLParser({
        ignoreAttributes: false,
        attributeNamePrefix: '@_',
        textNodeName: '#text',
        allowBooleanAttributes: true,
        parseTagValue: true,
        parseAttributeValue: true,
        trimValues: true,
      });
      parsed = parser.parse(text);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Invalid XML';
      throw ToolError.validation(`Invalid XML: ${msg}`, {
        suggestion: 'Check that all tags are properly closed and attributes are quoted',
      });
    }

    try {
      return stringifyYaml(parsed, { indent });
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to serialize YAML';
      throw ToolError.execution(msg);
    }
  },
};
